/**
 * 促銷表單「示意試算」：與 api promotionEngine commerceCartDiscount 邏輯對齊（不含其他規則先後折抵）。
 */
import { buildPromoConfig } from '@/composables/useCommerceAdvancedRules.js'

const DEMO_RUNNING = 3000
const DEMO_BASE = 3000
const VARIANT_OUT = '00000000-0000-4000-8000-00000000ffff'
const CAT_OUT = '00000000-0000-4000-8000-00000000ca99'

function normalizeRuleConfig(rule) {
  const c = rule.config
  if (c == null) return {}
  if (typeof c === 'object') return c
  if (typeof c === 'string') {
    try {
      return JSON.parse(c)
    } catch {
      return {}
    }
  }
  return {}
}

function filterEligibleLines(rule, ctx) {
  const cfg = normalizeRuleConfig(rule)
  const lf = cfg.lineFilter || cfg.line_filter || {}
  const mode = lf.mode || 'all'
  const lines = ctx.lines || []
  if (!mode || mode === 'all') return lines

  if (mode === 'variants') {
    const ids = new Set((lf.variantIds || lf.variant_ids || []).map((x) => String(x)))
    if (!ids.size) return []
    return lines.filter((l) => ids.has(String(l.variantId)))
  }

  if (mode === 'category') {
    const cid = lf.categoryId || lf.category_id
    if (!cid) return []
    const wantDesc = Boolean(lf.includeCategoryDescendants ?? lf.include_category_descendants)
    if (wantDesc && ctx.categorySubtreeByRoot) {
      const set = ctx.categorySubtreeByRoot.get(String(cid))
      if (!set || set.size === 0) {
        return lines.filter((l) => l.categoryId && String(l.categoryId) === String(cid))
      }
      return lines.filter((l) => l.categoryId && set.has(String(l.categoryId)))
    }
    return lines.filter((l) => l.categoryId && String(l.categoryId) === String(cid))
  }

  return lines
}

function commerceCartDiscount(rule, ctx) {
  const cfg = normalizeRuleConfig(rule)
  const usage = cfg.usageMode || cfg.usage_mode || 'auto'
  if (usage === 'coupon_template') return 0
  if (usage === 'fixed_code') {
    const code = String(cfg.promoCode || cfg.promo_code || '').trim()
    if (!code) return 0
    const triggers = (ctx.promoTriggerCodes || []).map((c) => String(c).trim().toLowerCase())
    if (!triggers.includes(code.toLowerCase())) return 0
  }

  const eligible = filterEligibleLines(rule, ctx)
  const eligibleQty = eligible.reduce((s, l) => s + Math.max(0, Number(l.qty) || 0), 0)
  const eligibleSubtotal = eligible.reduce((s, l) => s + Math.max(0, Number(l.lineTotal) || 0), 0)

  const lf = cfg.lineFilter || cfg.line_filter || {}
  const mode = lf.mode || 'all'
  if (mode !== 'all' && (!eligible.length || eligibleQty <= 0)) return 0

  const minQty = Math.max(0, Number(cfg.minQty ?? cfg.min_qty ?? 0))
  if (minQty > 0 && eligibleQty < minQty) return 0

  const minSpendRaw = cfg.minSpend ?? cfg.min_spend
  if (minSpendRaw != null && Number(minSpendRaw) > 0) {
    const baseCart = Number(ctx.baseSubtotal ?? ctx.runningSubtotal ?? 0)
    if (baseCart < Number(minSpendRaw)) return 0
  }

  const running = Math.max(0, Number(ctx.runningSubtotal) || 0)
  if (running <= 0) return 0

  const target = cfg.target === 'lines' ? 'lines' : 'order'
  let discountBase
  if (target === 'lines') {
    discountBase = Math.min(eligibleSubtotal, running)
  } else {
    discountBase = running
  }

  const dt = cfg.discountType || cfg.discount_type || 'fixed'
  let d = 0
  if (dt === 'percent') {
    const p = Math.max(0, Number(cfg.discountPercent ?? cfg.discount_percent ?? 0))
    d = discountBase * (p / 100)
  } else {
    d = Math.max(0, Number(cfg.discountAmount ?? cfg.discount_amount ?? 0))
  }

  return Math.max(0, Math.min(d, running))
}

function makeDemoLines(form) {
  const sel = form.selectedVariants || []
  const vIn = sel[0]?.variantId || '00000000-0000-4000-8000-000000000001'
  const vIn2 = sel.length >= 2 ? sel[1].variantId : VARIANT_OUT
  const cid = form.categoryId || '00000000-0000-4000-8000-00000000ca01'

  return [
    {
      variantId: vIn,
      categoryId: cid,
      qty: 2,
      lineTotal: 2000,
      label: '示意：範圍內',
    },
    {
      variantId: vIn2,
      categoryId: CAT_OUT,
      qty: 1,
      lineTotal: 1000,
      label: '示意：可能不在範圍內（對照）',
    },
  ]
}

/**
 * @param {Record<string, unknown>} form — 與 useCommerceAdvancedRules promoForm 相同形狀
 */
export function computePromoFormPreview(form) {
  const cfg = buildPromoConfig(form)
  const rule = { config: cfg }
  const lines = makeDemoLines(form)

  const usage = cfg.usageMode || 'auto'
  if (usage === 'coupon_template') {
    return {
      kind: 'coupon_template',
      headline: '優惠券模板',
      bodyLines: [
        '此模式僅儲存設定，結帳試算不會直接折價。',
        '實際折抵請在發券／兌券流程由優惠券模組處理。',
      ],
      discount: 0,
    }
  }

  const promoTriggerCodes =
    usage === 'fixed_code' && cfg.promoCode ? [cfg.promoCode] : [cfg.promoCode || 'SAMPLE'].filter(Boolean)

  const ctx = {
    lines,
    runningSubtotal: DEMO_RUNNING,
    baseSubtotal: DEMO_BASE,
    promoTriggerCodes,
  }

  const lf = cfg.lineFilter || {}
  const mode = lf.mode || 'all'

  const appMode = cfg.applicationMode === 'exclusive' ? 'exclusive' : 'stackable'
  const applicationModeLabel =
    appMode === 'exclusive' ? '主規則（整單僅一筆有折價）' : '加算（可與其他加算規則疊加）'

  const eligible = filterEligibleLines(rule, ctx)
  const eligibleQty = eligible.reduce((s, l) => s + Math.max(0, Number(l.qty) || 0), 0)
  const eligibleSubtotal = eligible.reduce((s, l) => s + Math.max(0, Number(l.lineTotal) || 0), 0)

  const checks = []
  if (appMode === 'exclusive') {
    checks.push(
      '主規則：若清單中已有較優先的主規則先產生折價，本條即使成立也不會再折。加算規則仍會接續評估。'
    )
  } else {
    checks.push('加算：會與其他加算規則依優先順序共用剩餘小計；主規則折價後仍可能再套用加算。')
  }
  let blocked = null

  if (usage === 'fixed_code') {
    const code = String(cfg.promoCode || '').trim()
    if (!code) {
      blocked = '請填寫折扣代碼；未填寫時不會折價（試算為 0）。'
    } else {
      checks.push(`代碼「${code}」：試算假設 API 已傳入 promoTriggerCodes（與代碼相同）。`)
    }
  }

  if (mode === 'variants' && (!lf.variantIds || lf.variantIds.length === 0)) {
    blocked = '指定規格模式下請至少選擇一筆規格，否則沒有符合的商品列。'
  }
  if (mode === 'category' && !lf.categoryId) {
    blocked = '請選擇商品分類，否則無法篩選符合列。'
  }

  const minQty = Math.max(0, Number(cfg.minQty) || 0)
  if (minQty > 0 && eligibleQty < minQty) {
    checks.push(`最少件數 ${minQty}：示意購物車符合範圍件數為 ${eligibleQty}，未達門檻故折價為 0。`)
  }

  const minSpend = cfg.minSpend
  if (minSpend != null && Number(minSpend) > 0) {
    if (DEMO_BASE < Number(minSpend)) {
      checks.push(`最低消費 ${minSpend} 元：示意整車小計 ${DEMO_BASE} 元，未達門檻故折價為 0。`)
    } else {
      checks.push(`最低消費 ${minSpend} 元：示意整車小計 ${DEMO_BASE} 元，已達門檻。`)
    }
  }

  const discount = commerceCartDiscount(rule, ctx)

  const target = cfg.target === 'lines' ? 'lines' : 'order'
  let discountBase = DEMO_RUNNING
  if (target === 'lines') {
    discountBase = Math.min(eligibleSubtotal, DEMO_RUNNING)
  }

  const dt = cfg.discountType === 'percent' ? 'percent' : 'fixed'
  const rawCalc =
    dt === 'percent'
      ? discountBase * (Math.max(0, Number(cfg.discountPercent) || 0) / 100)
      : Math.max(0, Number(cfg.discountAmount) || 0)
  const capped = Math.min(rawCalc, DEMO_RUNNING)

  return {
    kind: 'demo',
    headline: '示意試算（非實際訂單）',
    applicationModeLabel,
    demoCartTotal: DEMO_RUNNING,
    demoLines: lines.map((l) => ({
      label: l.label,
      qty: l.qty,
      lineTotal: l.lineTotal,
    })),
    eligibleQty,
    eligibleSubtotal,
    targetLabel: target === 'lines' ? '對指定範圍商品（列小計）' : '對整筆訂單',
    discountBase,
    discount,
    rawCalc,
    capped,
    discountType: dt,
    discountPercent: cfg.discountPercent,
    discountAmount: cfg.discountAmount,
    checks,
    blocked,
    scopeLabel:
      mode === 'all'
        ? '全館商品'
        : mode === 'variants'
          ? `指定規格（已選 ${(lf.variantIds || []).length} 筆）`
          : `指定分類${lf.includeCategoryDescendants ? '（含子分類）' : ''}`,
  }
}
